import React, { useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { FiAlertCircle, FiArrowLeft, FiArrowRight } from "react-icons/fi";
import Logo from "../../assets/images/LogoNew.svg";
import { getFormTypeLabel } from "../../util/applicationLabels";
import {
  buildEddBlockedMessage,
  formatEddDueDate,
  getEddRequestOptions,
  getEddRequestTestKey,
  getEddRequestTitle,
} from "./loginV2EddFlow";
import "./WorkableApplicationsScreen.scss";

const LoadingState = () => (
  <div
    className="auth-workable__list"
    data-testid="auth-edd-workable-requests-loading"
  >
    {[0, 1].map((item) => (
      <div className="auth-workable__card" key={item}>
        <Skeleton height={28} width="55%" />
        <Skeleton height={16} width="70%" />
        <Skeleton height={42} />
      </div>
    ))}
  </div>
);

const EmptyState = ({ message }) => (
  <div className="auth-workable__empty" data-testid="auth-edd-workable-requests-empty">
    <FiAlertCircle aria-hidden="true" />
    <span>{message}</span>
  </div>
);

const EddRequestCard = ({
  request,
  actionLoadingKey,
  onResume,
}) => {
  const testKey = getEddRequestTestKey(request);
  const recordId = request.eddRequestId;
  const title = getEddRequestTitle(request);
  const formTypeLabel = getFormTypeLabel(request.formType);
  const dueDateLabel = formatEddDueDate(request.dueDate);
  const isLoading = actionLoadingKey === `resume-edd:${testKey}`;

  return (
    <article
      className="auth-workable__card"
      data-testid={`auth-edd-request-card-${testKey}`}
      data-record-id={recordId || undefined}
    >
      <div className="auth-workable__card-main">
        <div className="auth-workable__content">
          <div className="auth-workable__card-labels">
            <span
              className="auth-workable__type-label"
              data-testid={`auth-edd-request-form-type-${testKey}`}
            >
              {formTypeLabel}
            </span>
            {dueDateLabel ? (
              <span
                className="auth-workable__reference"
                data-testid={`auth-edd-request-due-date-${testKey}`}
              >
                Due {dueDateLabel}
              </span>
            ) : null}
          </div>
          <h4 data-testid={`auth-edd-request-title-${testKey}`}>
            {title}
          </h4>
        </div>
      </div>
      <button
        type="button"
        className="auth-workable__action"
        data-testid={`auth-edd-request-resume-${testKey}`}
        data-record-id={recordId || undefined}
        disabled={isLoading}
        onClick={() => onResume?.(request)}
      >
        {isLoading ? "Opening..." : "Continue"}
        <FiArrowRight aria-hidden="true" />
      </button>
    </article>
  );
};

const EddWorkableRequestsScreen = ({
  decision,
  eddRequests = [],
  loading = false,
  actionLoadingKey = "",
  error = "",
  email = "",
  onResume,
  onBack,
}) => {
  const requestItems = useMemo(() => {
    const source = eddRequests?.length ? eddRequests : decision?.eddRequests || [];
    return getEddRequestOptions(source);
  }, [decision, eddRequests]);

  const hasRequests = requestItems.length > 0;
  const headerTitle = hasRequests ? "Choose periodic review" : "Periodic review";
  const headerDescription = email
    ? `Signed in as ${email}`
    : "Continue an available periodic review.";
  const pageErrorMessage = hasRequests ? error || "" : "";

  return (
    <div
      className="form-content-container auth-workable auth-edd-workable"
      data-testid="auth-edd-workable-requests"
    >
      <div className="form-content-header auth-workable__topbar">
        <img
          src={Logo}
          alt="fv-bank"
          className="logo logo-dark form-content-logo"
        />
      </div>

      <div className="form-content-body with-shadow auth-workable__shell">
        <div className="form-content-body-header auth-workable__title">
          {headerTitle}
        </div>
        <div className="form-content-body-content auth-workable__body-content">
          <div className="auth-workable__intro">
            <p data-testid="auth-edd-workable-requests-signed-in">{headerDescription}</p>
          </div>

          {pageErrorMessage ? (
            <div className="auth-workable__error" data-testid="auth-edd-workable-requests-error">
              <FiAlertCircle aria-hidden="true" />
              <span>{pageErrorMessage}</span>
            </div>
          ) : null}

          {loading ? (
            <LoadingState />
          ) : (
            <>
              {hasRequests ? (
                <div
                  className="auth-workable__list"
                  data-testid="auth-edd-workable-requests-list"
                >
                  {requestItems.map((request) => (
                    <EddRequestCard
                      key={`${request.eddRequestId}:${getEddRequestTestKey(request)}`}
                      request={request}
                      actionLoadingKey={actionLoadingKey}
                      onResume={onResume}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState message={buildEddBlockedMessage()} />
              )}
            </>
          )}

          {onBack ? (
            <div className="auth-workable__footer">
              <button
                type="button"
                className="auth-workable__back"
                data-testid="auth-edd-workable-requests-back"
                onClick={onBack}
              >
                <FiArrowLeft aria-hidden="true" />
                Sign in with another email
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default EddWorkableRequestsScreen;
